import type { AxiosInstance } from 'axios';
import type { TranslateFn, U2000ApiErrorResult } from '../u2000Core/axios/U2000HandleApiError';
import type { U2000SearchPayload } from '../u2000Core/components/tables/basic/U2000BasicTable';
import { U2000GenerateUrlUUID } from '../u2000Core/tools/U2000UrlHelpers';

const LOGICALUNIT_URL = 'U3153';

type LogicalUnitEndpointsType = {
  INITLU: string;
  SEARCH: string;
  GET: (id: string | number) => string;
  CREATE: string;
  UPDATE: (id: string | number) => string;
  DELETE: (id: string | number) => string;
  AUDITS: (id: string | number) => string;
};

const LogicalUnitEndpoints: LogicalUnitEndpointsType = {
  INITLU: `${LOGICALUNIT_URL}/initLU`,
  SEARCH: `${LOGICALUNIT_URL}/search`,
  GET: (id: string | number) => `${LOGICALUNIT_URL}/${id}`,
  CREATE: `${LOGICALUNIT_URL}/create`,
  UPDATE: (id: string | number) => `${LOGICALUNIT_URL}/update/${id}`,
  DELETE: (id: string | number) => `${LOGICALUNIT_URL}/delete/${id}`,
  AUDITS: (id: string | number) => `${LOGICALUNIT_URL}/${id}/audits`,
};

export type U3153InvoiceTypeNature = 'STANDARD' | 'CREDIT' | 'ADVANCE' | 'WIP_WRITE_OFF';

export const U3153_INVOICE_TYPE_NATURES: U3153InvoiceTypeNature[] = [
  'STANDARD',
  'CREDIT',
  'ADVANCE',
  'WIP_WRITE_OFF',
];

/** Codes système protégés (pas de delete / pas de changement de nature). */
export const U3153_SYSTEM_INVOICE_TYPE_CODES = ['FP', 'FF', 'NC', 'ADV', 'WWO'] as const;

export type U3153SystemInvoiceTypeCode = (typeof U3153_SYSTEM_INVOICE_TYPE_CODES)[number];

/** Réponse GET /U3153/initLU + logicalUnitCode fusionné côté shell. */
export interface U3153InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
  logicalUnitCode: string;
}

/** Ligne POST /U3153/search. */
export interface U3153InvoiceTypeSearchRow {
  id: string;
  code?: string;
  nature?: U3153InvoiceTypeNature | string;
  active?: boolean;
  invoiceTypeLabelFr?: string;
  invoiceTypeLabelEn?: string;
  version: number;
  deletedAt?: string | Date | null;
  createdAt?: string | Date;
}

export interface U3153InvoiceTypeTranslation {
  id?: string;
  languageCode: string;
  invoiceTypeLabel: string;
  version?: number;
}

/** Détail GET /U3153/:id (result). */
export interface U3153InvoiceType {
  id: string;
  code: string;
  nature: U3153InvoiceTypeNature;
  active: boolean;
  translations: U3153InvoiceTypeTranslation[];
  version: number;
}

export type U3153InvoiceTypeTranslationFormRow = U3153InvoiceTypeTranslation;

export type U3153InvoiceTypeFormRow = U3153InvoiceType;

export interface U3153InvoiceTypeCreateBody {
  code: string;
  nature: U3153InvoiceTypeNature;
  active: boolean;
  translations: Array<{
    languageCode: string;
    invoiceTypeLabel: string;
  }>;
}

export interface U3153InvoiceTypeUpdateBody {
  id: string;
  nature?: U3153InvoiceTypeNature;
  active: boolean;
  version: number;
  translations: Array<
    | {
        languageCode: string;
        invoiceTypeLabel: string;
      }
    | {
        id: string;
        languageCode: string;
        invoiceTypeLabel: string;
        version: number;
      }
  >;
}

export interface U3153InvoiceTypeDeleteBody {
  id: string;
  version: number;
}

export const U3153_ERROR_CODE_DUPLICATE = 'BU315301';
export const U3153_ERROR_CODE_IN_USE = 'BU315302';
export const U3153_ERROR_CODE_SYSTEM = 'BU315303';

export function u3153IsSystemInvoiceTypeCode(code: string | null | undefined): boolean {
  if (!code) return false;
  const normalized = code.trim().toUpperCase();
  return (U3153_SYSTEM_INVOICE_TYPE_CODES as readonly string[]).includes(normalized);
}

export function createEmptyU3153FormRow(): U3153InvoiceTypeFormRow {
  return {
    id: '',
    code: '',
    nature: 'STANDARD',
    active: true,
    version: 0,
    translations: [
      { languageCode: 'fr', invoiceTypeLabel: '' },
      { languageCode: 'en', invoiceTypeLabel: '' },
    ],
  };
}

export function normalizeU3153FromApi(row: U3153InvoiceType): U3153InvoiceType {
  return {
    ...row,
    nature: row.nature ?? 'STANDARD',
    active: row.active !== false,
    translations: Array.isArray(row.translations)
      ? row.translations.map((tr) => ({
          ...tr,
          languageCode: typeof tr.languageCode === 'string' ? tr.languageCode.trim().toLowerCase() : tr.languageCode,
        }))
      : [],
  };
}

function u3153TranslationLabel(
  translations: U3153InvoiceTypeTranslation[] | undefined,
  lang: 'fr' | 'en',
): string | undefined {
  if (!Array.isArray(translations)) return undefined;
  const tr = translations.find((t) => t.languageCode?.trim().toLowerCase() === lang);
  const desc = tr?.invoiceTypeLabel?.trim();
  return desc || undefined;
}

/** Reconstruit une ligne search (libellés FR/EN) à partir du détail fiche. */
export function u3153FormRowToSearchRow(
  row: U3153InvoiceType | U3153InvoiceTypeFormRow,
  existing?: U3153InvoiceTypeSearchRow | null,
): U3153InvoiceTypeSearchRow {
  const searchRow: U3153InvoiceTypeSearchRow = {
    id: row.id,
    code: row.code,
    nature: row.nature,
    active: row.active !== false,
    invoiceTypeLabelFr: u3153TranslationLabel(row.translations, 'fr'),
    invoiceTypeLabelEn: u3153TranslationLabel(row.translations, 'en'),
    version: row.version,
  };
  if (existing) {
    return { ...existing, ...searchRow };
  }
  return searchRow;
}

export function u3153FormatInvoiceTypeListLabel(row: U3153InvoiceTypeSearchRow, language: string): string {
  const code = row.code?.trim() ?? '';
  const isFr = language.startsWith('fr');
  const linguistic =
    (isFr ? row.invoiceTypeLabelFr : row.invoiceTypeLabelEn)?.trim() ||
    row.invoiceTypeLabelFr?.trim() ||
    row.invoiceTypeLabelEn?.trim() ||
    '';
  if (code && linguistic) return `${code} — ${linguistic}`;
  return code || linguistic;
}

export function buildCreateBody(data: U3153InvoiceTypeFormRow): U3153InvoiceTypeCreateBody {
  return {
    code: data.code.trim(),
    nature: data.nature,
    active: data.active !== false,
    translations: data.translations.map((tr) => ({
      languageCode: tr.languageCode.trim().toLowerCase(),
      invoiceTypeLabel: tr.invoiceTypeLabel.trim(),
    })),
  };
}

export function buildUpdateBody(data: U3153InvoiceTypeFormRow): U3153InvoiceTypeUpdateBody {
  const body: U3153InvoiceTypeUpdateBody = {
    id: data.id,
    active: data.active !== false,
    version: data.version,
    translations: data.translations.map((tr) => {
      const languageCode = tr.languageCode.trim().toLowerCase();
      const invoiceTypeLabel = tr.invoiceTypeLabel.trim();
      const hasId = Boolean(tr.id && String(tr.id).trim() !== '');
      if (hasId && tr.id) {
        return {
          id: tr.id,
          languageCode,
          invoiceTypeLabel,
          version: typeof tr.version === 'number' ? tr.version : 0,
        };
      }
      return { languageCode, invoiceTypeLabel };
    }),
  };
  if (!u3153IsSystemInvoiceTypeCode(data.code)) {
    body.nature = data.nature;
  }
  return body;
}

export function buildDeleteBody(data: U3153InvoiceTypeFormRow): U3153InvoiceTypeDeleteBody {
  return {
    id: data.id,
    version: data.version,
  };
}

export const doInitLU = (axios: AxiosInstance) => {
  return axios.get(LogicalUnitEndpoints.INITLU);
};

export const doSearch = (axios: AxiosInstance, searchPayload: U2000SearchPayload) => {
  return axios.post(LogicalUnitEndpoints.SEARCH, searchPayload);
};

export const doGetData = (axios: AxiosInstance, id: string | number, forceRefresh?: boolean) => {
  const params = {
    ...(forceRefresh && { b: U2000GenerateUrlUUID() }),
  };
  return axios.get(LogicalUnitEndpoints.GET(id), { params });
};

export const doCreateData = (axios: AxiosInstance, body: U3153InvoiceTypeCreateBody) => {
  return axios.post(LogicalUnitEndpoints.CREATE, body);
};

export const doUpdateData = (axios: AxiosInstance, id: string | number, body: U3153InvoiceTypeUpdateBody) => {
  return axios.post(LogicalUnitEndpoints.UPDATE(id), body);
};

export const doDeleteData = (axios: AxiosInstance, id: string | number, body: U3153InvoiceTypeDeleteBody) => {
  return axios.post(LogicalUnitEndpoints.DELETE(id), body);
};

export const doGetAuditsData = (axios: AxiosInstance, id: string | number) => {
  const params = {
    b: U2000GenerateUrlUUID(),
  };
  return axios.get(LogicalUnitEndpoints.AUDITS(id), { params });
};

export function luHandleAppiError(t: TranslateFn, nameSpace: string, err: unknown, errorResult: U2000ApiErrorResult) {
  const apiErr = err as { errorCode?: string; description?: string };
  switch (apiErr?.errorCode) {
    case U3153_ERROR_CODE_DUPLICATE:
      errorResult.fieldErrors['code'] = t('errInvoiceTypeCodeDuplicate', { ns: nameSpace });
      break;
    case U3153_ERROR_CODE_IN_USE:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errDeleteInUse', { ns: nameSpace }),
      });
      break;
    case U3153_ERROR_CODE_SYSTEM:
      errorResult.genericErrors.push({
        title: t('errorTitle', { ns: nameSpace }),
        text: t('errSystemInvoiceType', { ns: nameSpace }),
      });
      break;
    default:
      break;
  }
}
